import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailService } from '../services/email';
import EmailList from '../components/Email/EmailList';
import EmailView from '../components/Email/EmailView';
import { Loader2, RefreshCw, Inbox as InboxIcon, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Inbox() {
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    // Pagination State
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const LIMIT = 20;

    const navigate = useNavigate();

    useEffect(() => {
        fetchEmails(page);
    }, [page]);

    const fetchEmails = async (pageToFetch) => {
        try {
            if (emails.length === 0) setLoading(true);
            setError('');

            const response = await emailService.fetchEmails(pageToFetch, LIMIT);

            setEmails(response.data.emails);
            setTotalPages(response.data.totalPages);
            setPage(response.data.page);
        } catch (err) {
            console.error(err);
            setError(err.response?.status === 423 ? 'Mailbox locked. Retry shortly.' : 'Connection error.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        // Reset to page 1 on manual refresh
        setPage(1);
        fetchEmails(1);
    };

    const handleEmailSelect = async (email) => {
        setSelectedEmail({ ...email, isLoading: true });
        try {
            const response = await emailService.fetchEmail(email.id);
            setSelectedEmail(response.data);
        } catch (err) {
            console.error(err);
            // Optionally set error state here if viewing failed
        }
    };

    return (
        <div className="flex h-full bg-white rounded-tl-2xl overflow-hidden shadow-sm">
            {/* Left Column: Email List */}
            <div className="w-[400px] border-r border-gray-200 flex flex-col bg-white z-10">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0">
                    <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">Inbox</h2>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
                            Page {page}
                        </span>
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className={`p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-all ${refreshing ? 'animate-spin text-blue-600' : ''}`}
                        title="Refresh"
                    >
                        <RefreshCw className="h-5 w-5" />
                    </button>
                </div>

                {error && <div className="p-3 bg-red-50 text-red-600 text-xs text-center">{error}</div>}

                {/* List Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                            <Loader2 className="h-8 w-8 animate-spin mb-2" />
                            <span className="text-sm">Syncing emails...</span>
                        </div>
                    ) : (
                        <EmailList
                            emails={emails}
                            onSelectEmail={handleEmailSelect}
                            selectedEmailId={selectedEmail?.id}
                        />
                    )}
                </div>

                {/* Pagination Footer */}
                <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                    </button>

                    <span className="text-xs text-gray-500 font-medium">
                        {totalPages > 0 ? `Page ${page} of ${totalPages}` : 'No emails'}
                    </span>

                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || loading}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Next <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                </div>
            </div>

            {/* Right Column: Email View */}
            <div className="flex-1 bg-gray-50/50 relative">
                {selectedEmail ? (
                    selectedEmail.isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
                            <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
                        </div>
                    ) : (
                        <EmailView
                            email={selectedEmail}
                            onDelete={() => {/* impl delete */ }}
                            onReply={() => {/* impl reply */ }}
                            onForward={() => {/* impl forward */ }}
                        />
                    )
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-300">
                        <div className="bg-gray-100 p-6 rounded-full mb-4">
                            <InboxIcon className="h-16 w-16 text-gray-300" />
                        </div>
                        <p className="text-lg font-medium text-gray-400">Select an email to read</p>
                    </div>
                )}
            </div>
        </div>
    );
}